export const CATEGORY_PALETTE = [
  '#F476FF',
  '#FF8A00',
  '#00B4D8',
  '#90E0EF',
  '#06FFA5',
  '#FFD60A',
  '#F72585',
  '#7209B7',
];

export const EVENT_CATEGORY_DEFINITIONS = [
  { label: 'Live Music', color: CATEGORY_PALETTE[0] },
  { label: 'Art', color: CATEGORY_PALETTE[1] },
  { label: 'Theater', color: CATEGORY_PALETTE[2] },
  { label: 'Film', color: CATEGORY_PALETTE[3] },
  { label: 'Workshop', color: CATEGORY_PALETTE[4] },
  { label: 'Nightlife', color: CATEGORY_PALETTE[5] },
  { label: 'Secret', color: CATEGORY_PALETTE[6] },
  { label: 'Halloween', color: '#ff7a18' },
  { label: 'Giveaway', color: '#b20070' },
];

export const EVENT_CATEGORIES = EVENT_CATEGORY_DEFINITIONS.map(({ label }) => label);

const EVENT_CATEGORY_COLOR_BY_LABEL = new Map(
  EVENT_CATEGORY_DEFINITIONS.map(({ label, color }) => [label, color])
);

/** Labels that guest hiding and inference depend on. */
const PROTECTED_CATEGORY_LABELS = new Set(['secret', 'gatekeeped']);

/** from → to. Applied to definition labels and inferred labels. */
let categoryRenames = {};

export function setEventCategoryRenames(map) {
  const next = {};
  if (map && typeof map === 'object') {
    Object.entries(map).forEach(([from, to]) => {
      const source = String(from || '').trim();
      const target = String(to || '').trim();
      if (source && target) next[source] = target;
    });
  }
  categoryRenames = next;
}

export function getEventCategoryRenames() {
  return { ...categoryRenames };
}

export function resolveCategoryLabel(label) {
  let current = String(label || '').trim();
  const seen = new Set();
  while (categoryRenames[current] && !seen.has(current)) {
    seen.add(current);
    current = categoryRenames[current];
  }
  return current;
}

export function isProtectedCategoryLabel(label) {
  return PROTECTED_CATEGORY_LABELS.has(String(label || '').trim().toLowerCase());
}

/**
 * Reject empty, unchanged, duplicate, and Secret renames.
 * @returns {{ ok: true, from: string, to: string } | { ok: false, message: string }}
 */
export function validateCategoryRename(from, to, existingLabels = []) {
  const source = String(from || '').trim();
  const target = String(to || '').trim();
  if (!source || !target) {
    return { ok: false, message: 'Type a new category name.' };
  }
  if (isProtectedCategoryLabel(source) || isProtectedCategoryLabel(target)) {
    return { ok: false, message: 'Secret cannot be renamed.' };
  }
  if (source.toLowerCase() === target.toLowerCase()) {
    return { ok: false, message: 'That is already the category name.' };
  }
  const taken = (Array.isArray(existingLabels) ? existingLabels : []).some(
    (label) => String(label || '').trim().toLowerCase() === target.toLowerCase()
      && String(label || '').trim().toLowerCase() !== source.toLowerCase()
  );
  if (taken) {
    return { ok: false, message: 'A category with that name already exists.' };
  }
  return { ok: true, from: source, to: target };
}

function replaceLabel(label, from, to) {
  return String(label || '').trim().toLowerCase() === from.toLowerCase() ? to : label;
}

/**
 * Return updated category fields when an event uses `from`.
 * `null` when nothing on the doc changes.
 */
export function categoryRenamePatch(event, from, to) {
  const source = String(from || '').trim();
  const target = String(to || '').trim();
  if (!event || !source || !target) return null;
  const patch = {};
  let changed = false;

  if (Object.prototype.hasOwnProperty.call(event, 'categories')) {
    const next = normalizeCategoryList(event.categories).map((label) => replaceLabel(label, source, target));
    const deduped = normalizeCategoryList(next);
    if (deduped.join('\u0000') !== normalizeCategoryList(event.categories).join('\u0000')) {
      patch.categories = deduped;
      changed = true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(event, 'category')) {
    const current = event.category;
    if (Array.isArray(current)) {
      const next = normalizeCategoryList(current).map((label) => replaceLabel(label, source, target));
      const deduped = normalizeCategoryList(next);
      if (deduped.join('\u0000') !== normalizeCategoryList(current).join('\u0000')) {
        patch.category = deduped;
        changed = true;
      }
    } else if (typeof current === 'string' && current.trim().toLowerCase() === source.toLowerCase()) {
      patch.category = target;
      changed = true;
    }
  }

  return changed ? patch : null;
}

/**
 * Point every alias that landed on `from` at `to`, and record `from` → `to`.
 */
export function mergeCategoryRename(renames, from, to) {
  const source = String(from || '').trim();
  const target = String(to || '').trim();
  const next = { ...(renames && typeof renames === 'object' ? renames : {}) };
  Object.keys(next).forEach((key) => {
    if (String(next[key] || '').trim().toLowerCase() === source.toLowerCase()) {
      next[key] = target;
    }
  });
  next[source] = target;
  delete next[target];
  return next;
}

const INFERENCE_RULES = [
  {
    label: 'Live Music',
    keywords: ['concert', 'music', 'live', 'svirka'],
  },
  {
    label: 'Art',
    keywords: ['art', 'exhibition', 'galerija', 'izložba'],
  },
  {
    label: 'Theater',
    keywords: ['theater', 'theatre', 'pozorište', 'predstava'],
  },
  {
    label: 'Film',
    keywords: ['film', 'movie', 'cinema', 'kino'],
  },
  {
    label: 'Workshop',
    keywords: ['workshop', 'radionica'],
  },
  {
    label: 'Nightlife',
    keywords: ['party', 'club'],
  },
  {
    label: 'Halloween',
    keywords: ['halloween'],
  },
];

export function normalizeCategoryList(value) {
  const values = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  return Array.from(
    new Set(values.filter((item) => typeof item === 'string').map((item) => item.trim()).filter(Boolean))
  );
}

/**
 * Return explicitly stored categories.
 * `null` means no category field exists and legacy inference may run.
 * An empty array means the editor explicitly selected no categories.
 */
export function getStoredEventCategories(event) {
  if (!event || typeof event !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(event, 'categories')) {
    return normalizeCategoryList(event.categories);
  }
  if (Object.prototype.hasOwnProperty.call(event, 'category')) {
    return normalizeCategoryList(event.category);
  }
  return null;
}

export function inferCategoriesFromText(event) {
  const text = `${event?.title || ''} ${event?.description || ''}`.toLowerCase();
  return normalizeCategoryList(
    INFERENCE_RULES
      .filter(({ keywords }) => keywords.some((keyword) => text.includes(keyword)))
      .map(({ label }) => resolveCategoryLabel(label))
  );
}

export function toggleCategoryInList(categories, label) {
  const normalized = normalizeCategoryList(categories);
  return normalized.includes(label)
    ? normalized.filter((category) => category !== label)
    : [...normalized, label];
}

export function getCategoryColor(label) {
  const resolved = resolveCategoryLabel(label);
  if (EVENT_CATEGORY_COLOR_BY_LABEL.has(resolved)) {
    return EVENT_CATEGORY_COLOR_BY_LABEL.get(resolved);
  }
  if (EVENT_CATEGORY_COLOR_BY_LABEL.has(label)) {
    return EVENT_CATEGORY_COLOR_BY_LABEL.get(label);
  }

  const text = String(label || '');
  const hash = Array.from(text).reduce((total, character) => total + character.charCodeAt(0), 0);
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

/**
 * Count how many items include each category label.
 * @param {unknown[]} items
 * @param {(item: unknown) => string[]} getLabels
 * @returns {Map<string, number>}
 */
export function countCategoryLabels(items, getLabels) {
  const counts = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    normalizeCategoryList(getLabels(item)).forEach((label) => {
      counts.set(label, (counts.get(label) || 0) + 1);
    });
  });
  return counts;
}

/**
 * Build CategoryToggleGroup options with counts, sorted by count desc then label.
 * @param {{
 *   definitions?: Array<string | { label: string, color?: string }>,
 *   counts: Map<string, number> | Record<string, number>,
 *   includeZero?: boolean,
 * }} args
 * @returns {Array<{ label: string, color: string, count: number }>}
 */
export function buildCategoryToggleOptions({
  definitions = [],
  counts,
  includeZero = false,
}) {
  const countOf = (label) => resolvedCounts.get(label) || 0;

  const resolvedCounts = new Map();
  const addCount = (label, count) => {
    const resolved = resolveCategoryLabel(label);
    if (!resolved) return;
    resolvedCounts.set(resolved, (resolvedCounts.get(resolved) || 0) + (count || 0));
  };
  if (counts instanceof Map) {
    counts.forEach((count, label) => addCount(label, count));
  } else if (counts && typeof counts === 'object') {
    Object.entries(counts).forEach(([label, count]) => addCount(label, count));
  }

  const labels = new Set();
  definitions.forEach((option) => {
    const raw = typeof option === 'string' ? option : option?.label;
    const label = raw ? resolveCategoryLabel(raw) : '';
    if (label) labels.add(label);
  });
  resolvedCounts.forEach((_, label) => labels.add(label));

  return Array.from(labels)
    .map((label) => ({
      label,
      color: getCategoryColor(label),
      count: countOf(label),
    }))
    .filter((option) => includeZero || option.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * Sum of all category assignment counts (for an "All N" chip).
 * @param {Map<string, number> | Record<string, number> | Array<{ count?: number }>} countsOrOptions
 * @returns {number}
 */
export function sumCategoryCounts(countsOrOptions) {
  if (Array.isArray(countsOrOptions)) {
    return countsOrOptions.reduce((total, option) => total + (option?.count || 0), 0);
  }
  if (countsOrOptions instanceof Map) {
    let total = 0;
    countsOrOptions.forEach((count) => {
      total += count || 0;
    });
    return total;
  }
  if (countsOrOptions && typeof countsOrOptions === 'object') {
    return Object.values(countsOrOptions).reduce((total, count) => total + (count || 0), 0);
  }
  return 0;
}

/**
 * Check if an event is tagged with the "Secret" category.
 * Also accepts legacy "Gatekeeped" until Firestore is fully migrated.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {boolean}
 */
export function isSecretEvent(event) {
  if (!event || typeof event !== 'object') return false;
  const categories = getStoredEventCategories(event) || inferCategoriesFromText(event);
  return categories.some((category) => {
    const normalized = String(category).toLowerCase();
    return normalized === 'secret' || normalized === 'gatekeeped';
  });
}
